import { format } from 'date-fns';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import type { BirdCertificate } from '../services/reports';
import type { CreateBirdDto } from '../api/birds';
import { BirdStatus, Gender, GenderDeterminationMethod } from './enums';

export const CERTIFICATE_TRANSFER_FORMAT = 'birdman-certificate-transfer';
export const CERTIFICATE_TRANSFER_VERSION = 1;

export interface BirdCertificateTransferPackage {
  format: typeof CERTIFICATE_TRANSFER_FORMAT;
  version: typeof CERTIFICATE_TRANSFER_VERSION;
  exportedAt: string;
  sourceInstance?: string;
  /** Deep link on the exporting instance (may require auth on that tenant). */
  sourceUrl?: string;
  certificate: BirdCertificate;
}

export function buildBirdCertificateShareUrl(birdId: string, origin = window.location.origin): string {
  const url = new URL('/reports', origin);
  url.searchParams.set('report', 'bird-certificate');
  url.searchParams.set('birdId', birdId);
  return url.toString();
}

export function buildCertificateFilename(cert: BirdCertificate): string {
  const band = (cert.bird.identification.bandId || cert.bird.bandNumber || 'bird')
    .replace(/[^\w.-]+/g, '_');
  const date = format(new Date(cert.issueDate || new Date()), 'yyyy-MM-dd');
  return `BirdCertificate_${band}_${date}`;
}

export function buildTransferPackage(
  cert: BirdCertificate,
  opts?: { sourceInstance?: string; sourceUrl?: string },
): BirdCertificateTransferPackage {
  return {
    format: CERTIFICATE_TRANSFER_FORMAT,
    version: CERTIFICATE_TRANSFER_VERSION,
    exportedAt: new Date().toISOString(),
    sourceInstance: opts?.sourceInstance,
    sourceUrl: opts?.sourceUrl,
    certificate: cert,
  };
}

export function parseTransferPackage(raw: unknown): BirdCertificateTransferPackage {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid certificate file');
  }
  const pkg = raw as Partial<BirdCertificateTransferPackage>;
  if (pkg.format !== CERTIFICATE_TRANSFER_FORMAT) {
    throw new Error('Not a BirdMan certificate transfer file');
  }
  if (!pkg.certificate?.bird) {
    throw new Error('Certificate payload is missing bird data');
  }
  return pkg as BirdCertificateTransferPackage;
}

export async function renderElementToPdfBlob(element: HTMLElement): Promise<Blob> {
  const scale = 2;
  const canvas = await html2canvas(element, {
    scale,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
  });

  const rootRect = element.getBoundingClientRect();
  const breakSelectors = [
    '.cert-header',
    '.cert-meta-grid',
    '.cert-section',
    '.cert-subsection',
    '.cert-footer',
  ];
  const sectionBreaks = breakSelectors
    .flatMap((selector) => Array.from(element.querySelectorAll(selector)))
    .map((el) => Math.round((el.getBoundingClientRect().bottom - rootRect.top) * scale))
    .filter((y) => y > 0 && y < canvas.height);

  const breaks = [0, ...Array.from(new Set(sectionBreaks)).sort((a, b) => a - b), canvas.height];

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 8;
  const usableWidth = pageWidth - margin * 2;
  const maxSliceHeightPx = ((pageHeight - margin * 2) * canvas.width) / usableWidth;

  let sliceStart = 0;
  let pageIndex = 0;

  while (sliceStart < canvas.height - 1) {
    const maxEnd = sliceStart + maxSliceHeightPx;
    let sliceEnd = canvas.height;

    const candidates = breaks.filter((b) => b > sliceStart && b <= maxEnd);
    if (candidates.length > 0) {
      sliceEnd = candidates[candidates.length - 1];
    } else {
      const nextBreak = breaks.find((b) => b > sliceStart);
      if (nextBreak && nextBreak - sliceStart <= maxSliceHeightPx) {
        sliceEnd = nextBreak;
      } else {
        sliceEnd = Math.min(maxEnd, canvas.height);
      }
    }

    if (sliceEnd <= sliceStart) {
      sliceEnd = Math.min(sliceStart + maxSliceHeightPx, canvas.height);
    }

    const sliceHeight = sliceEnd - sliceStart;
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceHeight;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) throw new Error('Failed to render PDF slice');
    ctx.drawImage(
      canvas,
      0,
      sliceStart,
      canvas.width,
      sliceHeight,
      0,
      0,
      canvas.width,
      sliceHeight,
    );

    const imgData = sliceCanvas.toDataURL('image/png');
    const sliceHeightMm = (sliceHeight * usableWidth) / canvas.width;

    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(imgData, 'PNG', margin, margin, usableWidth, sliceHeightMm);

    sliceStart = sliceEnd;
    pageIndex += 1;
  }

  return pdf.output('blob');
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function openCertificateMailto(opts: {
  title: string;
  birdId: string;
  bandId?: string;
  shareUrl: string;
  pdfFilename?: string;
}): void {
  const bandLine = opts.bandId ? `Band / ID: ${opts.bandId}\n` : '';
  const attachLine = opts.pdfFilename
    ? `\nA PDF named "${opts.pdfFilename}" was downloaded — please attach it to this email before sending.\n`
    : '\n';
  const subject = encodeURIComponent(opts.title);
  const body = encodeURIComponent(
    `I wanted to share this bird certificate with you.\n\n` +
      `${opts.title}\n` +
      bandLine +
      `Generated on ${format(new Date(), 'PPP')}\n` +
      attachLine +
      `\nOpen the certificate in BirdMan (sign-in required if access control is enabled):\n` +
      `${opts.shareUrl}\n\n` +
      `Tip: Ask the seller for a BirdMan certificate transfer (.json) file to import lineage into your own instance without retyping.`,
  );
  window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
}

function mapGender(sex?: string): Gender {
  const value = (sex || '').toUpperCase();
  if (value === 'MALE' || value === 'M') return Gender.MALE;
  if (value === 'FEMALE' || value === 'F') return Gender.FEMALE;
  return Gender.UNKNOWN;
}

function mapGenderMethod(method?: string): GenderDeterminationMethod {
  const value = (method || '').toUpperCase();
  if (value.includes('DNA')) return GenderDeterminationMethod.DNA_SEXING;
  if (value.includes('SURG')) return GenderDeterminationMethod.SURGICAL_SEXING;
  if (value.includes('VIS')) return GenderDeterminationMethod.VISUALLY_SEXED;
  return GenderDeterminationMethod.NOT_SEXED;
}

function toIsoDate(value?: string): string {
  if (!value) return new Date().toISOString().slice(0, 10);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return new Date().toISOString().slice(0, 10);
  return d.toISOString().slice(0, 10);
}

/**
 * Maps a transfer certificate into CreateBirdDto fields for the buyer's instance.
 * `speciesId` must be resolved locally (IDs differ across tenants).
 */
export function mapCertificateToCreateBirdDraft(
  cert: BirdCertificate,
  speciesId: string,
): CreateBirdDto {
  const bird = cert.bird;
  const hatch = toIsoDate(bird.biologicalInfo?.hatchDate);
  const band = toIsoDate(bird.biologicalInfo?.bandingDate || hatch);
  const notes = [
    `Imported from BirdMan certificate ${cert.certificateNumber}`,
    bird.lineage?.father?.bandNumber ? `Sire band: ${bird.lineage.father.bandNumber}` : null,
    bird.lineage?.mother?.bandNumber ? `Dam band: ${bird.lineage.mother.bandNumber}` : null,
    bird.provenance?.notes || null,
  ]
    .filter(Boolean)
    .join('\n');

  return {
    bandId: bird.identification?.bandId || bird.bandNumber || `IMPORT-${Date.now()}`,
    speciesId,
    mutationName: bird.biologicalInfo?.colorMutation || undefined,
    gender: mapGender(bird.biologicalInfo?.sex),
    genderDeterminationMethod: mapGenderMethod(bird.biologicalInfo?.sexDeterminationMethod),
    status: BirdStatus.AVAILABLE,
    sellPrice: bird.valuation?.estimatedValue,
    hatchingDate: hatch,
    bandingDate: band,
    cageNumber: bird.status?.location || 'IMPORT',
    phenotype: bird.biologicalInfo?.phenotype || undefined,
    genotype: bird.biologicalInfo?.genotype || undefined,
    parentIds: [],
    siblingIds: [],
    descendantIds: [],
    photos: [],
    provenance: {
      type: 'BOUGHT',
      sellerName: bird.provenance?.sellerName || bird.breeder?.facilityName,
      sellerContact: bird.provenance?.sellerContact || bird.breeder?.email || bird.breeder?.phone,
      sellerAddress: bird.provenance?.sellerAddress || bird.breeder?.address,
      dateAcquired: toIsoDate(bird.provenance?.dateAcquired || cert.issueDate),
      price: bird.provenance?.purchasePrice ?? bird.valuation?.estimatedValue,
      notes,
      purchaseOrderNumber: bird.provenance?.purchaseOrderNumber,
      invoiceNumber: bird.provenance?.invoiceNumber,
      paymentMethod: bird.provenance?.paymentMethod,
      paymentReference: bird.provenance?.paymentReference,
      transactionId: bird.provenance?.transactionId,
      batchNumber: bird.provenance?.batchNumber,
      warrantyInfo: bird.provenance?.warrantyInfo,
      healthCertificateNumber:
        bird.provenance?.healthCertificateNumber || cert.certificateNumber,
      quarantinePeriod: bird.provenance?.quarantinePeriod,
      quarantineStartDate: bird.provenance?.quarantineStartDate
        ? toIsoDate(bird.provenance.quarantineStartDate)
        : undefined,
      quarantineEndDate: bird.provenance?.quarantineEndDate
        ? toIsoDate(bird.provenance.quarantineEndDate)
        : undefined,
    },
  };
}

export function matchSpeciesId(
  speciesList: Array<{
    id: string;
    commonName?: string;
    scientificName?: string;
    fullTaxonomicName?: string;
    genus?: string;
    species?: string;
  }>,
  cert: BirdCertificate,
): string | null {
  const common = (cert.bird.species?.commonName || '').trim().toLowerCase();
  const scientific = (cert.bird.species?.scientificName || '').trim().toLowerCase();
  const byScientific = speciesList.find((s) => {
    const label = (
      s.scientificName ||
      s.fullTaxonomicName ||
      [s.genus, s.species].filter(Boolean).join(' ')
    )
      .trim()
      .toLowerCase();
    return !!scientific && label === scientific;
  });
  if (byScientific) return byScientific.id;
  const byCommon = speciesList.find(
    (s) => (s.commonName || '').trim().toLowerCase() === common && !!common,
  );
  return byCommon?.id ?? null;
}
